/* eslint-disable @typescript-eslint/member-ordering */
import { environment } from '@env/environment';
import { StorageService } from '@app-core/services/storage/storage.service';
import { AccessService } from '@app-core/services/access/access.service';

// Singleton instances
let storageInstance: StorageService | null = null;
let accessServiceInstance: AccessService | null = null;

export function initializeApiServices(storage: StorageService, access: AccessService) {
  storageInstance = storage;
  accessServiceInstance = access;
}

export class API {
  public static LEGACY_API_SERVER_URL = environment.legacy_api_server_url;
  private static _intermediateServerUrl: string | null = null;
  private static _urlCache: Record<string, string> = {};

  private static MAPBOX_GEOCODING_API_URL = `https://api.mapbox.com/search/geocode/v6`;

  /**
   * Optional manual refresh (only needed after programmatic region change)
   */
  public static refreshRegion(region?: string): void {
    try {
      const awsCognitoRegion =
        region || (storageInstance?.getStorageValue('AWS_COGNITO_REGION_KEY') as string) || accessServiceInstance?.getAwsCognitoRegion();
      const regionConfig = awsCognitoRegion && environment.cognitoConfigs[awsCognitoRegion];
      this._intermediateServerUrl = regionConfig?.intermediate_server_url || environment.intermediate_server_url;
      this._urlCache = {}; // clear cached URLs
    } catch (err) {
      console.error('Error refreshing region:', err);
      this._intermediateServerUrl = environment.intermediate_server_url;
    }
  }

  public static get INTERMEDIATE_SERVER_URL(): string {
    if (!this._intermediateServerUrl) {
      try {
        const awsCognitoRegion =
          (storageInstance?.getStorageValue('AWS_COGNITO_REGION_KEY') as string) || accessServiceInstance?.getAwsCognitoRegion();
        const regionConfig = awsCognitoRegion && environment.cognitoConfigs[awsCognitoRegion];
        this._intermediateServerUrl = regionConfig?.intermediate_server_url || environment.intermediate_server_url;
        this._urlCache = {}; // clear cache
      } catch (err) {
        this._intermediateServerUrl = environment.intermediate_server_url;
      }
    }

    return this._intermediateServerUrl || environment.intermediate_server_url;
  }

  public static VERSION = '/v3';
  public static PATH = `/api${API.VERSION}`;

  public static getUrl(path: string): string {
    if (!this._urlCache[path]) {
      const url = `${this.INTERMEDIATE_SERVER_URL}${path}`;
      this._urlCache[path] = url;
    }
    return this._urlCache[path];
  }

  // Example endpoint (keep others unchanged)
  public static get GET_FLEET_STATS(): string {
    return this.getUrl('/v2/fleet-stats');
  }
  public static get GET_DRIVER_LIST(): string {
    return this.getUrl('/driverslist');
  }
  public static get GET_EVENT_FREQUENCY(): string {
    return this.getUrl('/eventfrequency');
  }
  public static get GET_FLEET_EVENT_TREND(): string {
    return this.getUrl('/v2/fleet-event-trend');
  }
  public static get GET_DRIVER_EVENT_TREND(): string {
    return this.getUrl('/v2/driver-event-trend');
  }
  public static get GET_VIOLATIONS(): string {
    return this.getUrl('/v2/violations');
  }
  public static get GET_ASSET_VIOLATIONS(): string {
    return this.getUrl('/asset-violations');
  }
  public static get GET_SEVERE_VIOLATIONS(): string {
    return this.getUrl('/recommended-events');
  }
  public static get REQUESTED_VIDEOS(): string {
    return this.getUrl('/create-rate-limited-request');
  }
  public static get GET_CRITICAL_EVENT(): string {
    return this.getUrl('/critical-events');
  }

  // Authenticate
  public static get INTERMEDIATE_SERVER_LOGIN(): string {
    return this.getUrl('/authenticate/fleetmanager');
  }
  public static get INTERMEDIATE_SERVER_MASTER_LOGIN_AS(): string {
    return this.getUrl('/authenticate/master');
  }
  public static get INTERMEDIATE_SERVER_ADMIN_LOGIN_AS(): string {
    return `${environment.intermediate_server_url}/authenticate/admin`;
  }
  public static get refreshAccessToken(): string {
    return this.getUrl('/token/refresh');
  }
  public static VALIDATE_REDIRECTION(token: string): string {
    return this.getUrl(`/validate-redirection/${token}`);
  }

  public static AUTHENTICATE_SSO_USER = (tspName: string) => this.getUrl(`/authenticate-sso-user/${tspName}`);
  public static AUTHENTICATE_GEOTAB_USER = () => this.getUrl(`/v2/authenticate-geotab`);
  public static AUTHENTICATE_RSA_SSO_USER = (tspName: string) => this.getUrl(`/authenticate-rsa-sso-user/${tspName}`);

  /** @deprecated Use EULA_CONSENT_V2 instead. @see API.EULA_CONSENT_V2 */
  public static get EULA_CONSENT(): string {
    return this.getUrl('/eula-consent');
  }
  public static get EULA_CONSENT_V2(): string {
    return this.getUrl('/v2/eula-consent');
  }

  public static get GET_FLEET_DETAILS(): string {
    return this.getUrl('/fleet-prefrences');
  }

  public static get UPDATE_FLEET_DETAILS(): string {
    return this.getUrl('/fleet-prefrences-update');
  }

  // AWS Congnito endpoints
  public static oauth2Authorization = (domain: string) => `${domain}/oauth2/authorize`;
  public static oauth2Logout = (domain: string) => `${domain}/logout`;

  // Driver Detail
  public static get GET_TRIP_LIST_V2(): string {
    return this.getUrl('/fleet-trips');
  }
  public static get GET_DRIVER_LIST_V2(): string {
    return this.getUrl('/fleet-drivers');
  }
  public static get CREATE_DVR_REQUEST(): string {
    return this.getUrl('/create-dvr-request');
  }
  public static get CREATE_EDVR_REQUEST(): string {
    return this.getUrl('/create-edvr-request');
  }
  public static get CREATE_LOCAL_DVR_REQUEST(): string {
    return this.getUrl('/create-local-dvr-request');
  }
  public static get GET_DRIVER_STATS_V2(): string {
    return this.getUrl('/v2/driver-stats');
  }
  public static get GET_DRIVER_TRIPS_V2(): string {
    return this.getUrl('/driver-trips');
  }
  public static get GET_ASSET_TRIPS_V2(): string {
    return this.getUrl('/asset-trips');
  }
  public static get GET_ASSET_STATS(): string {
    return this.getUrl('/asset-stats');
  }
  public static get GET_LATEST_TRIPS_BY_ASSET_ID(): string {
    return this.getUrl('/latest-trips-by-asset-id');
  }
  public static GET_DRIVER_DETAILS_V2 = (driverId: string) => this.getUrl(`/v2/external-driver/${driverId}`);
  public static get GET_AI_RECOGNIZED_EVENTS(): string {
    return this.getUrl('/ai-coaching-heeded-stats');
  }
  public static get GET_EVENT_STREAK(): string {
    return this.getUrl('/driver-streaks');
  }
  public static GET_DRIVER = (driverId: string) => this.getUrl(`/manual-drivers/${driverId}`);

  // Trip Detail
  public static get TRIP_DETAILS(): string {
    return this.getUrl('/trip-details');
  }
  public static get EVENT_DETAILS(): string {
    return this.getUrl('/event-details');
  }
  public static get UPDATE_TRIP(): string {
    return this.getUrl('/update-trip');
  }
  public static UPDATE_TAGS_TO_TRIP = (tripId: string) => this.getUrl(`/v2/trips/${tripId}/tags`);
  public static UPDATE_DRIVER_TO_TRIP = (driverId: string, tripId: string) =>
    this.getUrl(`/drivers/${driverId}/trips/${tripId}/update-trip`);
  public static PREVIEW_UPDATE_DRIVER_ASSET_TRIPS = (assetId: string) => this.getUrl(`/preview-asset-trips/${assetId}`);
  public static UPDATE_DRIVER_ASSET_TRIPS = (assetId: string) => this.getUrl(`/update-asset-trips/${assetId}`);
  public static get SAMPLE_BULK_ASSET_TRIP_UPDATION_CSV(): string {
    return this.getUrl('/sample-update-asset-trips-csv');
  }
  public static get UPLOAD_BULK_ASSET_TRIP_UPDATION_CSV(): string {
    return this.getUrl('/update-asset-trips-csv');
  }
  public static get DRIVER_UPDATION_LOGS(): string {
    return this.getUrl('/list-update-driver-requests');
  }

  public static get GET_BUCKET_CONTENT(): string {
    return `${this.LEGACY_API_SERVER_URL}/api/v5/lm/GetBucketContent`;
  }

  // Get security questions API
  public static get SECURITY_QUESTIONS(): string {
    return `${this.LEGACY_API_SERVER_URL}/api/security-questions`;
  }
  public static get SECURITY_QUESTIONS_FOR_A_USER(): string {
    return `${this.LEGACY_API_SERVER_URL}/api/security-questions/`;
  }

  // Validate security answers API
  public static get VALIDATE_ANSWERS(): string {
    return `${this.LEGACY_API_SERVER_URL}/api/security-questions/validate-answers`;
  }

  // Asset configuration
  public static get GET_FLEET_CONFIGURATION(): string {
    return this.getUrl('/configuration');
  }
  public static get GET_ASSET_CONFIGURATION(): string {
    return this.getUrl('/get-asset-configuration');
  }
  public static get GET_FLEET_CUSTOM_EVENT_CONFIGURATION(): string {
    return this.getUrl('/custom-events');
  }
  public static get GET_FLEET_EVENTS_CUSTOM_EVENTS(): string {
    return this.getUrl('/fleet-events');
  }
  public static get GET_AUDIO_ALERT(): string {
    return this.getUrl('/audio-alerts');
  }

  // package config white label backend apis
  public static get GET_FLEET_ASSETS(): string {
    return this.getUrl('/assets');
  }
  public static get PATCH_ASSET(): string {
    return this.getUrl('/assets');
  }
  public static get UPLOAD_ASSSETS(): string {
    return this.getUrl('/assets');
  }

  // user configuration
  public static get GET_UNIQUE_DRIVERS_FOR_A_USER(): string {
    return `${this.LEGACY_API_SERVER_URL}/api/v3/GetUniqueDriversForaUser`;
  }
  public static get GET_USER_CONFIG_FOR_DRIVER(): string {
    return `${this.LEGACY_API_SERVER_URL}/api/v3/GetUserConfigForDriver`;
  }
  public static get SET_USER_CONFIGURATION(): string {
    return `${this.LEGACY_API_SERVER_URL}/api/v3/SetUserConfiguration`;
  }
  public static get SET_GLOBAL_USER_CONFIGURATION(): string {
    return `${this.LEGACY_API_SERVER_URL}/api/v3/SetGlobalUserConfiguration`;
  }

  public static get ENROLL_DRIVER(): string {
    return this.getUrl('/driver/face-recognition/enroll');
  }
  public static get GET_DRIVER_IMAGES(): string {
    return this.getUrl('/driver/face-recognition/details');
  }
  public static get DELETE_PERSON_DETAILS(): string {
    return this.getUrl('/driver/face-recognition/delete');
  }

  // user configuration V2
  public static get DELETE_PERSON_DETAILS_V2(): string {
    return this.getUrl('/v2/driver/face-recognition/delete');
  }

  // Asset configuration
  public static get UPDATE_FLEET_CONFIGURATION(): string {
    return this.getUrl('/configuration');
  }
  public static get UPDATE_ASSET_CONFIGURATION(): string {
    return this.getUrl('/update-asset-configuration');
  }
  public static get UPDATE_CUSTOM_EVENT_CONFIGURATION(): string {
    return this.getUrl('/fleet-custom-events/bulk-update');
  }

  // Announcement Banner
  public static get GET_ANNOUNCEMENTS(): string {
    return this.getUrl('/announcements');
  }

  // DVR and E-DVR requests
  public static get GET_DVR_REQUESTS(): string {
    return this.getUrl('/fleet-upload-requests');
  }
  public static get GET_EDVR_REQUESTS(): string {
    return this.getUrl('/fleet-edvr-requests');
  }

  // FR feedback
  public static get TRIP_FR_FEEDBACK(): string {
    return this.getUrl('/trip-fr-feedback');
  }

  // Event Metadata
  public static get UPDATE_EVENT_METADATA(): string {
    return this.getUrl('/update-event-metadata');
  }

  // DVR Metadata
  public static get UPDATE_DVR_METADATA(): string {
    return this.getUrl('/update-dvr-metadata');
  }

  // GEO JSON - INDIA MAP
  public static get GEO_JSON(): string {
    return 'assets/common/geojson-india.json';
  }

  // TRANSLATE JSON
  public static get TRANSLATE_JSON(): string[] {
    return [
      'assets/i18n/en.json',
      'assets/i18n/es.json',
      'assets/i18n/pt.json',
      'assets/i18n/fr.json',
      'assets/i18n/pt-BR.json',
      'assets/i18n/fr-CA.json',
      'assets/i18n/en-GB.json',
      'assets/i18n/es-419.json',
      'assets/i18n/ja.json',
      'assets/i18n/ko.json',
    ];
  }

  // External Events
  public static get GET_EXTERNAL_EVENTS(): string {
    return this.getUrl('/external-events');
  }

  // Asset autocomplete
  public static get ASSETS_AUTOCOMPLETE(): string {
    return this.getUrl('/autocomplete/assets');
  }

  //Drivers Autocomplete
  public static get DRIVERS_AUTOCOMPLETE(): string {
    return this.getUrl('/autocomplete/drivers');
  }

  // DRIVER SIGNUP
  public static get PROVISION_DRIVER(): string {
    return this.getUrl('/provision-driver');
  }

  // GET REGISTERED DRIVERS
  public static get GET_MANUAL_DRIVERS(): string {
    return this.getUrl('/manual-drivers');
  }
  public static get GET_REGISTERED_DRIVERS(): string {
    return this.getUrl('/registered-drivers');
  }

  // UPDATE DRIVER DETAILS
  public static get UPDATE_DRIVER_DETAILS(): string {
    return this.getUrl('/update-driver');
  }

  // RESEND DRIVER TEMP PASSWORD EMAIL
  public static get RESEND_DRIVER_TEMP_PASSWORD(): string {
    return this.getUrl('/resend-driver-temp-password');
  }

  // LIVESTREAM
  public static get requestLivestream(): string {
    return this.getUrl('/live/create-stream-request');
  }
  public static get stopLivestream(): string {
    return this.getUrl('/live/stop-stream');
  }
  public static get REVIEW_LIVESTREAM(): string {
    return this.getUrl('/live/hls-session');
  }
  public static get LIVESTREAM_DETAILS(): string {
    return this.getUrl('/live/stream-details');
  }
  public static get GET_LATEST_TRIPS_ASSET_LIST(): string {
    return this.getUrl('/latest-trips');
  }
  public static get DEVICE_USAGE(): string {
    return this.getUrl('/device-usage');
  }
  public static REQUEST_WEBRTC_STREAM = API.INTERMEDIATE_SERVER_URL + '/v2/live/create-stream-request';
  public static JOIN_WEBRTC_STREAM = API.INTERMEDIATE_SERVER_URL + '/live/join-webrtc-stream';
  public static STOP_LIVE_STREAM = API.INTERMEDIATE_SERVER_URL + '/live/stop-stream';

  // DVR DETAILS
  public static get DVR_DETAILS(): string {
    return this.getUrl('/dvr-details');
  }

  // REVERSE GEOCODE
  public static get REVERSE_GEOCODE(): string {
    return `https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/reverseGeocode?f=json`;
  }

  // GENERATE S3 SIGNED URLS
  public static get GENERATE_S3_SIGNED_URLS(): string {
    return this.getUrl('/s3-signed-urls/generate');
  }

  // DELETE DRIVER
  public static DELETE_USER = (username: string) => this.getUrl(`/users/${username}`);

  // DVR AVAIBILITY
  public static get CHECK_DVR_AVAIBILITY(): string {
    return this.getUrl('/tsp/device-tracking-events');
  }

  // COACHING INCIDENTS
  public static get GET_COACHING_INCIDENTS(): string {
    return this.getUrl('/v2/coaching-events');
  }
  public static get GET_CHALLENGED_INCIDENTS(): string {
    return this.getUrl('/v2/challenged-events');
  }
  public static get GENERATE_SAMPLE_URLS(): string {
    return this.getUrl('/generate-sample-urls');
  }

  // ASSETS
  public static get MODIFY_DEVICE_MAPPING(): string {
    return this.getUrl('/fleet/remap-device');
  }
  public static get EXPORT_ASSETS(): string {
    return this.getUrl('/fleet/export-assets');
  }
  public static MANAGE_DEVICE = (deviceId: string) => this.getUrl(`/manage-device/${deviceId}`);
  public static TRIGGER_DEVICE_TASK = (deviceId: string) => this.getUrl(`/devices/${deviceId}/tasks`);
  public static GET_DEVICE_TASK_STATUS = (deviceId: string) => this.getUrl(`/devices/${deviceId}/tasks`);
  public static get SAMPLE_ASSET_CSV_DOWNLOAD(): string {
    return this.getUrl('/sample-asset-csv');
  }
  public static get BATCH_ASSET_UPDATION(): string {
    return this.getUrl('/update-asset-csv');
  }
  public static get ASSET_PLANS(): string {
    return this.getUrl('/asset-plans');
  }
  public static get DEVICE_MODEL_CONFIG(): string {
    return this.getUrl('/device-model-config');
  }
  public static get UPDATE_MDVR_CONFIG(): string {
    return this.getUrl('/mdvr-config');
  }

  //DIAGNOSTICS
  public static GET_HEALTH_TRACKING_EVENTS_LIST = (deviceId: string) =>
    `${API.INTERMEDIATE_SERVER_URL}/diagnostics/devices/${deviceId}/tracking-events`;
  //fleet health api's
  public static get GET_FLEET_HEALTH_Agg(): string {
    return this.getUrl('/health-events-aggregate');
  }
  public static GET_FLEET_HEALTH_LIST = (eventType: string) => this.getUrl(`/health-events/${eventType}`);

  //camera event apis
  public static get GET_CAMERA_EVENT_Agg(): string {
    return this.getUrl('/camera-events-aggregate');
  }
  public static get GET_CAMERA_EVENT_LIST(): string {
    return this.getUrl('/camera-events');
  }

  //camera online status
  public static get GET_CAMERA_ONLINE_STATUS_LIST(): string {
    return this.getUrl('/device-ping-list');
  }

  //Total Devices API for fleet health
  public static get GET_TOTAL_DEVCIES(): string {
    return this.getUrl('/fleet/device-stats');
  }

  //Total Current Issues API for fleet health
  public static get GET_TOTAL_CURRENT_ISSUES(): string {
    return this.getUrl('/device-health-issue-aggregate');
  }

  //Glossary of the Diagnostics
  public static GET_GLOSSARY = (healthEventId: string) => this.getUrl(`/health-events-faqs/${healthEventId}`);

  public static get GET_IMPROVED_DRIVERS(): string {
    return this.getUrl('/v2/improved-drivers');
  }
  public static get GET_FLEET_DEVICE_STATS(): string {
    return this.getUrl('/fleet/device-stats');
  }
  public static GET_ASSET_TAGS = (assetID: string) => this.getUrl(`/v2/asset/${assetID}/tags`);
  // deice tracking events for device management
  public static GET_DEVICE_TRACKING_EVENTS = API.INTERMEDIATE_SERVER_URL + '/tsp/device-tracking-events';

  //Mute events
  public static get GET_MUTED_EVENTS_LIST(): string {
    return this.getUrl('/v2/muted-events');
  }
  public static UPDATE_MUTE_STATE = (deviceId: string) => this.getUrl(`/mute-events/${deviceId}`);

  // REPORTS
  public static get GET_ARCHIVED_REPORTS(): string {
    return this.getUrl('/reports/archives');
  }
  public static get GET_ARCHIVED_REPORTS_AGGREGATE(): string {
    return this.getUrl('/reports/archives/aggregate');
  }
  public static get GET_FLEET_SAFETY_REPORT(): string {
    return this.getUrl('/reports/fleet-safety-report');
  }
  public static get GET_ASSET_DETAILS(): string {
    return this.getUrl('/asset-details');
  }
  public static get GET_DEVICE_DETAILS(): string {
    return this.getUrl('/device-details');
  }

  // FAQ
  public static GET_FAQ_DETAILS = (faqId: string) => this.getUrl(`/faq/${faqId}`);
  public static get EXPORT_TRIPS(): string {
    return this.getUrl('/fleet/export-trip-list');
  }
  public static GET_COFIG_FAQ_DETAILS = (eventId: string) => this.getUrl(`/sdk-configs-faq/${eventId}`);

  public static get SAMPLE_DRIVER_CSV_DOWNLOAD(): string {
    return this.getUrl('/unified-driver-sample-csv');
  }
  public static get BATCH_DRIVER_ADDITION(): string {
    return this.getUrl('/batch-unified-drivers');
  }

  // MFA
  public static get USER_MFA_SETTINGS(): string {
    return this.getUrl('/mfa/status');
  }
  public static get MFA_ENABLE(): string {
    return this.getUrl('/mfa/enable');
  }
  public static get MFA_DISABLE(): string {
    return this.getUrl('/mfa/disable');
  }
  public static adminDisableMFA = (email: string) => this.getUrl(`/users/${email}/admin-disable-mfa`);
  public static get CHANGE_PASSWORD(): string {
    return this.getUrl('/users/change-password');
  }

  // User Management
  public static get GET_USERS(): string {
    return this.getUrl('/users');
  }
  public static GET_SPECIFIC_USER = (email: string) => this.getUrl(`/users/${email}`);
  public static get CREATE_USER(): string {
    return this.getUrl('/users');
  }
  public static get EXPORT_USERS(): string {
    return this.getUrl('/users/export');
  }
  public static UPDATE_USER = (email: string) => this.getUrl(`/users/${email}`);
  public static DELET_USER = (email: string) => this.getUrl(`/users/${email}`);
  public static RESEND_TEMP_PASSWORD = (email: string) => this.getUrl(`/users/${email}/resend-temp-password`);
  public static get exportUsers(): string {
    return this.getUrl('/users/export');
  }

  // User Management V2
  public static get GET_USERS_V2(): string {
    return this.getUrl('/v2/users');
  }
  public static GET_SPECIFIC_USER_V2 = (userId: string) => this.getUrl(`/v2/users/${userId}`);
  public static get CREATE_USER_V2(): string {
    return this.getUrl('/v2/users');
  }
  public static UPDATE_USER_V2 = (userId: string) => this.getUrl(`/v2/users/${userId}`);
  public static DELETE_USER_V2 = (userId: string) => this.getUrl(`/v2/users/${userId}`);
  public static get EXPORT_USERS_V2(): string {
    return this.getUrl('/v2/users/export');
  }

  public static get GET_DRIVER_PERMISSIONS(): string {
    return this.getUrl('/driver-permission');
  }
  public static get UPDATE_DRIVER_PERMISSIONS(): string {
    return this.getUrl('/driver-permission');
  }
  public static get SAVE_DRIVER_CONFIG_CONSENT(): string {
    return this.getUrl('/driver-config-consent');
  }
  public static get GET_DRIVER_CONFIG_CONSENT(): string {
    return this.getUrl('/driver-config-consent');
  }

  public static get BATCH_DEVICE_PROVISIONING(): string {
    return this.getUrl('/batch-provision-devices');
  }
  public static get PROVISIONED_SAMPLE_CSV(): string {
    return this.getUrl('/sample-device-csv');
  }
  public static get PROVISION_DEVICE(): string {
    return this.getUrl('/provision-device');
  }
  public static get deprovisionAsset(): string {
    return this.getUrl('/deprovision-device');
  }
  public static get GET_FLEET_DEVICES_PAGINATED(): string {
    return this.getUrl('/v2/fleet/devices');
  }

  // Role Management
  public static get GET_ALLOWED_PERMISSION_LIST(): string {
    return this.getUrl('/v2/permissions');
  }
  public static get rolesList(): string {
    return this.getUrl('/v2/roles');
  }
  public static get roleHierarchy(): string {
    return this.getUrl('/v2/role-hierarchy');
  }
  public static get roleTemplate(): string {
    return this.getUrl('/v2/role-templates');
  }
  public static get addRole(): string {
    return this.getUrl('/v2/roles');
  }
  public static getRoleDetails = (roleId: string) => this.getUrl(`/v2/roles/${roleId}`);
  public static updateRole = (roleId: string) => this.getUrl(`/v2/roles/${roleId}`);
  public static deleteRole = (roleId: string) => this.getUrl(`/v2/roles/${roleId}`);

  // Tag Management
  public static UPDATE_TAGS_FOR_EVENT = (tripId: string, eventIndex: string) =>
    this.getUrl(`/v2/trips/${tripId}/events/${eventIndex}/tags`);
  public static UPDATE_DRIVER_TAGS = (driverId: string) => this.getUrl(`/v2/external-driver/${driverId}`);
  public static get GET_COACHING_CONFIG(): string {
    return this.getUrl('/fleetdriver/fleet-coaching-config');
  }
  public static get UPDATE_COACHING_CONFIG(): string {
    return this.getUrl('/fleetdriver/fleet-coaching-config');
  }
  public static get ADD_ATTRIBUTES(): string {
    return this.getUrl('/v2/attributes');
  }
  public static DELETE_ATTRIBUTE_NAME = (attributeId: string) => this.getUrl(`/v2/attributes/${attributeId}`);
  public static GET_ATTRIBUTE_DETAILS = (attributeId: string) => this.getUrl(`/v2/attributes/${attributeId}`);
  public static DEACTIVATE_ATTRIBUTE = (attributeId: string) => this.getUrl(`/v2/attributes/${attributeId}`);
  public static get LINK_ATTRIBUTES(): string {
    return this.getUrl('/v2/manage-entities');
  }
  public static get GET_ALL_TAGLIST(): string {
    return this.getUrl('/v2/tags');
  }
  public static get GET_UNIQUE_TAGLIST(): string {
    return this.getUrl('/v2/unique-tags');
  }
  public static get ADD_TAGS_TO_ATTRIBUTE(): string {
    return this.getUrl('/v2/tags');
  }
  public static UPDATE_TAGS = (tagId: string) => this.getUrl(`/v2/tags/${tagId}`);
  public static DELETE_TAGS = (tagId: string) => this.getUrl(`/v2/tags/${tagId}`);
  public static get GET_ENTITY_DETAILS(): string {
    return this.getUrl('/v2/entities');
  }
  public static get GET_ATTRIBUTES_V2(): string {
    return this.getUrl('/v2/attributes');
  }
  public static get ADD_WIDGET_TICKET(): string {
    return this.getUrl('/user-feedback');
  }
  public static GET_TAG_DETAILS = (rootTagId: string) => this.getUrl(`/v2/tags/${rootTagId}`);

  // coaching driver
  public static GET_COACHABLE_INCIDENTS = (driverId: string) => this.getUrl(`/fleetdriver/coaching-session/drivers/${driverId}/events`);
  public static GET_COACHABLE_INCIDENTS_COACH_VIEW = () => this.getUrl(`/v2/driver-self-coaching-coach-view-v2`);
  public static GET_DRIVER_DETAILS = (driverId: string) => this.getUrl(`/v2/external-driver/${driverId}`);
  public static get CREATE_COACHING_SESSION(): string {
    return this.getUrl('/fleetdriver/coaching-session');
  }
  public static UPDATE_COACHING_SESSION(): string {
    return this.getUrl('/v2/complete-coaching-session');
  }
  public static get LIST_COACHING_SESSION(): string {
    return this.getUrl('/fleetdriver/coaching-sessions');
  }
  public static get COACHING_RECPMMENDATIONS(): string {
    return this.getUrl('/fleetdriver/coaching-recommendations');
  }

  // Installation Report
  public static get GET_INSTALLATION_REPORT(): string {
    return this.getUrl('/installation-report');
  }

  public static COACHING_SESSION_DETAILS = (coachinSessionId: number) => this.getUrl(`/coaching-sessions/${coachinSessionId}`);

  public static GET_COACHING_EFFECTIVEESS_DRIVER_REPORT = (coachinSessionId: number, driverId: string) =>
    this.getUrl(`/drivers/${driverId}/coaching-sessions/${coachinSessionId}/driver-coaching-effectiveness`);
  public static GET_DRIVER_TRIPS_AGGREGATE = (driverId: string) => this.getUrl(`/drivers/${driverId}/aggregate`);
  public static get GET_COACHING_SESSION_LIST(): string {
    return this.getUrl('/coaching-sessions');
  }
  public static GET_FCOACHING_SESSION_REPORT = (coachinSessionId: number) =>
    this.getUrl(`/reports/coaching-session-report/${coachinSessionId}`);

  //Async Download of reports
  public static get ASYNC_REPORTS(): string {
    return this.getUrl('/async-reports');
  }

  // Client configuration
  public static get CLIENT_CONFIG(): string {
    return this.getUrl('/client-config');
  }

  //scheduled reports
  public static SCHEDULED_REPORT_ID = (scheduleId: string) => this.getUrl(`/scheduled-reports/${scheduleId}`);
  public static get SCHEDULED_REPORTS(): string {
    return this.getUrl('/scheduled-reports');
  }

  //Unified Driver List
  public static get GET_UNIFIED_DRIVERS(): string {
    return this.getUrl('/unified-driver-list');
  }
  public static get DELETE_UNIFIED_DRIVERS(): string {
    return this.getUrl('/unified-driver');
  }
  public static get REGISTER_DRIVER(): string {
    return this.getUrl('/unified-driver');
  }
  public static getdriverDetails = (driverId: string) => this.getUrl(`/v2/driver/${driverId}`);

  public static get UPDATE_DRIVER(): string {
    return this.getUrl('/unified-driver');
  }

  public static get NOTIFICATION_CONFIGURATION(): string {
    return this.getUrl('/notification-configuration');
  }

  public static get UPDATE_DEFAULT_CONFIGURATION(): string {
    return this.getUrl('/default-configuration');
  }

  public static SSO_USER = (ssouserHash: string) => this.getUrl(`/ssousers/${ssouserHash}`);

  // User Activity Log List & View Details
  public static get GET_USERS_LOG_LIST(): string {
    return this.getUrl('/user-activity-log/list');
  }

  public static get GET_USERS_LOG_DETAILS(): string {
    return this.getUrl('/user-activity-log/details');
  }

  // Coach Assignment
  public static get PERMANENT_COACH_ASSIGNMENT(): string {
    return this.getUrl('/coach-assignment');
  }

  public static get BULK_ASSIGNMENT(): string {
    return this.getUrl('/bulk-coach-assignment');
  }

  //QR code
  public static GET_GENERATE_QR_CODE = (driverId: string) => API.INTERMEDIATE_SERVER_URL + `/generate-qr-code/${driverId}`;

  //api to get suprsend distinct id and subscriber id
  public static SUPRSEND_DETAILS = API.INTERMEDIATE_SERVER_URL + '/get-distinct-id';

  public static get LOGIN_LOGS(): string {
    return this.getUrl('/login-logs');
  }
  // Custom Reports List Api
  public static GET_CUSTOM_REPORTS_LIST(params: { fleetId: string; limit: number; offset: number }) {
    return this.getUrl(`/fleets/${params.fleetId}/custom-reports/list?limit=${params.limit}&offset=${params.offset}`);
  }
  // Delete Custom Report
  public static DELETE_CUSTOM_REPORT(fleetId: string, customReportId: string) {
    return this.getUrl(`/fleets/${fleetId}/custom-reports/${customReportId}`);
  }
  // Download Custom Report
  public static DOWNLOAD_CUSTOM_REPORT(fleetId: string) {
    return this.getUrl(`/fleets/${fleetId}/async-reports/download`);
  }
  // Save Report
  public static SAVE_CUSTOM_REPORT(fleetId: string) {
    return this.getUrl(`/fleets/${fleetId}/custom-reports`);
  }
  // Talk to Data API's
  public static get GET_TALK_TO_DATA(): string {
    return this.getUrl('/llm-chat/talk-to-data');
  }
  public static get GET_SUGGESTED_QUESTIONS(): string {
    return this.getUrl('/llm-chat/suggested-questions');
  }
  public static get POST_LLM_FEEDBACK(): string {
    return this.getUrl('/llm-chat/feedback');
  }
  public static get POST_GENERATE_REPORT_NAME(): string {
    return this.getUrl('/llm-chat/generate-report-name');
  }
  public static DOWNLOAD_CUSTOM_REPORT_LIST_DATA() {
    return this.getUrl(`/llm-chat/edit-query`);
  }
  public static get POST_EDIT_QUERY(): string {
    return this.getUrl('/llm-chat/edit-query');
  }
  public static get POST_REPORT_CONVERSATION(): string {
    return this.getUrl('/llm-chat/report-conversation');
  }
  public static get GET_REPORT_CONVERSATION(): string {
    return this.getUrl('/llm-chat/report-conversation');
  }
  public static get DELETE_REPORT_CONVERSATION(): string {
    return this.getUrl('/llm-chat/report-conversation');
  }

  //tagging hierarchy

  public static get GET_HIERARCHY_TAGLIST(): string {
    return this.getUrl('/tags-hierarchy');
  }
  public static get SEARCH_TAGS(): string {
    return this.getUrl('/search-tags');
  }

  public static get ASSET_DRIVER_COUNT(): string {
    return this.getUrl('/asset-driver-count');
  }

  public static GET_CUSTOM_REPORT_DATA(customReportId: string) {
    return this.getUrl(`/custom-reports/preview?customReportId=${customReportId}`);
  }

  public static EDIT_CUSTOM_REPORT(customReportId: string) {
    return this.getUrl(`/custom-reports/${customReportId}`);
  }
  public static get GENERATE_VISUALS(): string {
    return this.getUrl('/llm-chat/generate-visuals');
  }
  public static get GENERATE_INSIGHTS(): string {
    return this.getUrl('/llm-chat/generate-insights');
  }
  /** GEOFENCE API - START */
  public static get CREATE_GEOFENCE(): string {
    return this.getUrl(`/geofence`);
  }
  public static UPDATE_GEOFENCE(geofenceId: number): string {
    return this.getUrl(`/geofence/${geofenceId}`);
  }
  public static get LIST_GEOFENCE(): string {
    return this.getUrl('/geofence');
  }
  public static DELETE_GEOFENCE(geofenceId: number): string {
    return this.getUrl(`/geofence/${geofenceId}`);
  }
  public static get CREATE_GEOFENCE_SETTING(): string {
    return this.getUrl(`/geofence/setting`);
  }
  public static UPDATE_GEOFENCE_SETTING(settingId: number): string {
    return this.getUrl(`/geofence/setting/${settingId}`);
  }
  public static get LIST_GEOFENCE_SETTING(): string {
    return this.getUrl(`/geofence/setting`);
  }
  public static DELETE_GEOFENCE_SETTING(settingId: number): string {
    return this.getUrl(`/geofence/setting/${settingId}`);
  }
  public static get LIST_GEOFENCE_ACTIVITY(): string {
    return this.getUrl('/geofence/activity');
  }
  public static get LIST_GEOFENCE_LOCATION(): string {
    return this.getUrl('/geofence/locations');
  }
  public static GET_LATLNG_FROM_ADDRESS(): string {
    return `${this.MAPBOX_GEOCODING_API_URL}/forward`;
  }
  public static GET_ADDRESS_FROM_LATLNG(): string {
    return `${this.MAPBOX_GEOCODING_API_URL}/reverse`;
  }
  /** GEOFENCE API - END */
}
